import { useCallback, useContext, useEffect, useState } from 'react';
import { fetchAuthenticated } from '../../../../controllers';
import { WAS_BACK_END_API_URL, URL_VERSION, WEB_OWNER } from '../../../../constants';
import { PatientProps, UserProps } from 'src/shared/types/types';
import { convertDateTime } from '../../../../components/common/convertDateTime/convertDateTime';
import './InfusionNotes.scss';
import { InfusionNoteDTO } from '.';
import { GlobalUserContext } from '../../../../shared/contexts/GlobalUserContext';
import { encryptObject, decryptObject } from '../../../../components/Encryption/obfuscationHandler';
import { Note } from '../../../../components/WysiwygEditor/note/Note';
import { AddNote } from '../../../../components/WysiwygEditor/note/addNote/AddNote';
import { v4 } from 'uuid';
import { BannerMessageContext } from 'src/shared/contexts/BannerMessageContext';
import { PopupMessageContext } from 'src/shared/contexts/PopupMessageContext';

interface InfusionNoteData {
  _id: string;
  _version: number;
  user: UserProps;
  patient: PatientProps;
  createdDateTime: string;
  lastModifiedDateTime?: string;
  lastModifiedBy?: string; // user ID
  archived: boolean;
  archivedDateTime?: string;
  archivedBy?: string; // user ID
  encrypt_note: string;
  type: string;
}

interface GetInfusionNotesResponse {
  status: string;
  total: number;
  // items: WithId<Document>[] | InfusionNote[]; // todo what about WithId?
  items: InfusionNoteData[];
  errors: string[];
}

interface InfusionNotesAjaxResult {
  result: GetInfusionNotesResponse;
}

interface InfusionNotesProps {
  patientId?: string;
  token: string | null;
}

export const InfusionNotes = ({ patientId, token }: InfusionNotesProps) => {
  const { onCloud } = useContext(GlobalUserContext);
  const [infusionNotes, setInfusionNotes] = useState<InfusionNoteDTO[]>([]);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [apiData, setApiData] = useState<InfusionNotesAjaxResult>();
  const [serverError, setServerError] = useState();
  const { addBannerMessage } = useContext(BannerMessageContext);
  const { addPopupMessage } = useContext(PopupMessageContext);

  const getInfusionNotes = useCallback(async () => {
    const configGet: RequestInit = {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    if (!patientId) return;
    setIsLoading(true);
    try {
      const infusionNotesURL = WAS_BACK_END_API_URL + `${URL_VERSION}/patients/` + patientId + '/infusions/notes';

      const response = await fetchAuthenticated<InfusionNotesAjaxResult>(infusionNotesURL, configGet);

      setIsLoading(false);
      if (response?.ok) {
        setApiData(response.parsedBody);
      }
    } catch (error) {
      addBannerMessage({
        id: v4(),
        message: 'Uh oh! There was an error downloading the infusion notes. Please refresh to try again.',
        type: 'error',
      });
    }
  }, [patientId, token, addBannerMessage]);

  useEffect(() => {
    try {
      getInfusionNotes();
    } catch (error: any) {
      setServerError(error);
    }
  }, [getInfusionNotes]);

  const [isLoadingNewNote, setIsLoadingNewNote] = useState<boolean>(false);

  const [isLoadingEditNote, setIsLoadingEditNote] = useState<boolean>(false);

  useEffect(() => {
    async function decryptData() {
      if (apiData?.result?.items) {
        try {
          await decryptObject(apiData.result, token, null);
          setInfusionNotes(
            apiData.result.items.map((item) => {
              return {
                key: item._id,
                _version: item?._version,
                author: item.user.name,
                createdDateTime: item?.createdDateTime,
                encrypt_note: item.encrypt_note,
                editedDateTime: item?.lastModifiedDateTime ? convertDateTime(item?.lastModifiedDateTime) : null,
              };
            })
          );
        } catch {
          setInfusionNotes([]);
        }
      }
    }
    decryptData();
  }, [apiData, token]);

  interface PutNoteResponse {
    result: {
      note: {
        _id: string;
        _version: number;
        user: {
          name: string;
        };
        createdDateTime: string;
        lastModifiedDateTime: string;
        encrypt_note: string;
      };
    };
  }

  const handleAddNoteSubmit = (noteBody: string): Promise<boolean> => {
    // we return a promise to help our AddNoteComponent clear the Wysiwyg Editor's state if the submission process succeeds

    setIsLoadingNewNote(true);

    return new Promise<boolean>(async (resolve, reject) => {
      const infusionNotesURL = WAS_BACK_END_API_URL + `${URL_VERSION}/patients/` + patientId + '/infusions/notes';

      const response = await fetchAuthenticated<PutNoteResponse>(infusionNotesURL, {
        method: 'PUT',
        body: JSON.stringify(
          await encryptObject(
            {
              encrypt_note: noteBody,
              owner: WEB_OWNER,
            },
            token,
            null
          )
        ),
        // Auth headers are manually entered for now, fetchAuthenticated will soon be updated to add them implicitly.
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setIsLoadingNewNote(false);
      if (!response?.ok) {
        addPopupMessage({
          id: v4(),
          header: 'Addition',
          message: 'Adding new Setup Note Failed. Please retry request.',
        });
        reject('Note did not post');
      }
      if (response.parsedBody === undefined) return;
      await decryptObject(response.parsedBody?.result, token, null);
      const data = response.parsedBody;
      const item = data.result.note;
      const newNote = {
        key: item?._id,
        _version: item?._version,
        author: item?.user.name,
        createdDateTime: item?.createdDateTime,
        encrypt_note: item?.encrypt_note,
        editedDateTime: item?.lastModifiedDateTime ? convertDateTime(item?.lastModifiedDateTime) : null,
      };
      const newNotes = [...infusionNotes, newNote];
      setInfusionNotes(newNotes);
      resolve(true);
    });
  };

  const handleEditNoteSubmit = (noteId: string, _version: number, noteBody: string): Promise<boolean> => {
    const infusionNoteURL = `${WAS_BACK_END_API_URL}${URL_VERSION}/patients/${patientId}/infusions/notes/${noteId}`;
    setIsLoadingEditNote(true);

    return new Promise<boolean>(async (resolve, reject) => {
      const response = await fetchAuthenticated(infusionNoteURL, {
        method: 'PATCH',
        body: JSON.stringify(
          await encryptObject(
            {
              _version,
              encrypt_note: noteBody,
              owner: WEB_OWNER,
            },
            token,
            null
          )
        ),
        // Auth headers are manually entered for now, fetchAuthenticated will soon be updated to add them implicitly.
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setIsLoadingEditNote(false);
      if (!response?.ok) {
        addPopupMessage({
          id: v4(),
          header: 'Change',
          message: 'Change to Setup Note Failed. Please retry request.',
        });
        reject('Note did not post');
      }
      // update the existing note list with new note data
      setInfusionNotes(
        infusionNotes.map((note) => {
          if (note.key !== noteId) return note;

          const newEditTime = new Date().toISOString();
          return {
            ...note,
            _version: note._version + 1,
            body: noteBody,
            editedDateTime: newEditTime,
          };
        })
      );
      resolve(true);
    });
  };

  const handleDeleteNoteSubmit = (noteId: string, _version: number): Promise<boolean> => {
    const infusionNoteURL = `${WAS_BACK_END_API_URL}${URL_VERSION}/patients/${patientId}/infusions/notes/${noteId}`;

    return new Promise<boolean>(async (resolve, reject) => {
      const deleteBody = await encryptObject(
        {
          _version,
          archive: true,
          owner: WEB_OWNER,
        },
        token,
        null
      );
      const response = await fetchAuthenticated(infusionNoteURL, {
        method: 'DELETE',
        // Auth headers are manually entered for now, fetchAuthenticated will soon be updated to add them implicitly.
        body: JSON.stringify(deleteBody),
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      try {
        if (!response?.ok) reject();
        setInfusionNotes(
          infusionNotes.filter((note: InfusionNoteDTO) => {
            return note.key !== noteId;
          })
        );
        resolve(true);
      } catch (e) {
        addPopupMessage({
          id: v4(),
          header: 'Delete',
          message: 'Deleting the Setup Note Failed. Please retry request.',
        });
        reject(e);
      }
    });
  };

  return (
    <div className="notes-card">
      {isLoading && <p>Loading ...</p>}
      {serverError && <p>Error:</p>} {/* TODO: add error text */}
      {!isLoading && !serverError && (
        <div className="notes-container">
          <div className="notes-list">
            {infusionNotes.map((item: InfusionNoteDTO) => {
              return (
                <Note
                  key={item.key}
                  data={{
                    key: item.key,
                    _version: item._version,
                    author: item.author,
                    createdDateTime: item.createdDateTime,
                    editedDateTime: item.editedDateTime,
                    encrypt_note: item.encrypt_note,
                  }}
                  isLoadingEditNote={isLoadingEditNote}
                  onNoteEdit={handleEditNoteSubmit}
                  onNoteDelete={handleDeleteNoteSubmit}
                />
              );
            })}
          </div>
          {!onCloud && <AddNote isLoadingNewNote={isLoadingNewNote} onSubmit={handleAddNoteSubmit} />}
        </div>
      )}
    </div>
  );
};
